/**
 * The PLP side navigation is a sticky, scrollable column: it has to start where
 * the chrome above it ends and be exactly the remaining viewport tall, or it
 * hangs past the fold (taking its own scrollbar with it) or leaves a gap. Both
 * numbers therefore come off a single measured offset — the bottom edge of the
 * chrome — so they cannot drift apart (ECOM-2796).
 */

/**
 * Stands in until the chrome has been measured on the client, and matches what
 * the hardcoded height used to assume: a one-line banner under a condensed
 * header.
 */
export const SIDE_NAVIGATION_FALLBACK_OFFSET = 130

export const buildSideNavigationStyle = (
  offset: number,
): { top: string; height: string } => {
  // A measured offset of zero means nothing has been measured yet (the header
  // is always present on a PLP, so its bottom is always positive once laid
  // out). Fall back until then.
  const resolved =
    offset > 0 ? Math.round(offset) : SIDE_NAVIGATION_FALLBACK_OFFSET

  return {
    top: `${resolved}px`,
    height: `calc(100vh - ${resolved}px)`,
  }
}

/**
 * The offset is the header's bottom edge in viewport coordinates. That single
 * number captures *every* piece of chrome stacked above the PLP content —
 * including non-sticky bars that sit above the sticky banner and header, like
 * Madeleine's benefits/USP bar, which the previous height sum (banner + header)
 * missed and left the navigation hanging past the fold (ECOM-2796).
 *
 * Because it is a viewport position rather than an element size, it changes as
 * the page scrolls — the non-sticky bar slides out of view and the header
 * condenses — so the caller must re-measure on scroll as well as on resize.
 * (Reading it only once, on layout, is the trap the earlier size-based approach
 * guarded against; re-reading on every scroll and resize is what keeps it from
 * going stale.)
 *
 * `content` is the header's content wrapper: while the header holds a fixed box
 * its contents spill lower when expanded, so whichever reaches further down is
 * where the chrome actually ends. The header's own `scrollHeight` would be the
 * obvious shortcut and is a trap — it also counts the absolutely positioned
 * flyout menu, so it leaps by hundreds of pixels the moment a shopper hovers the
 * navigation.
 */
export const measureChromeOffset = (
  header: HTMLElement,
  content?: HTMLElement | null,
): number => {
  const headerBottom = header.getBoundingClientRect().bottom
  const contentBottom = content ? content.getBoundingClientRect().bottom : 0

  return Math.max(0, headerBottom, contentBottom)
}
